import React, { useEffect, useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchTitles, fetchDetail } from "../../../redux/slices/userGuideSlice";
import styled from "styled-components";
import IconButton from "../../common/Button";
import { SearchIcon, DownArrowIcon } from "../../common/Icons";

const UserGuide = () => {
  const dispatch = useDispatch();
  const { titles, details } = useSelector((s) => s.userGuide);
  const [openId, setOpenId] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const term = searchInput.trim().toLowerCase();

  // 1) only on first render
  useEffect(() => {
    dispatch(fetchTitles());
  }, [dispatch]);

  // 2) narrow list by title OR content
  const filtered = useMemo(() => {
    return titles.filter(({ id, title }) => {
      const lcTitle = title.toLowerCase();
      const lcContent = details[id]?.content.toLowerCase() || "";
      return lcTitle.includes(term) || lcContent.includes(term);
    });
  }, [titles, details, term]);

  // 3) when searching, pre-fetch any missing detail so we can highlight inside content
  useEffect(() => {
    if (!term) return;
    filtered.forEach(({ id }) => {
      if (!details[id]) dispatch(fetchDetail(id));
    });
  }, [term, filtered, details, dispatch]);

  // 4) utility to wrap matches in <mark>
  const highlight = (text) => {
    if (!term) return text;
    const re = new RegExp(`(${term})`, "gi");
    return text.replace(re, "<mark>$1</mark>");
  };

  // 5) clicking outside of search still toggles manually; when searching auto-open all
  const isSearchMode = Boolean(term);

  const handleSearchChange = (e) => setSearchInput(e.target.value);

  const handleSupport = () => {
    window.location.href = "mailto:support@digitalcareplans.org.uk";
  };

  return (
    <div className="draftplan-container">
      <div className="search-filter-bar">
        <div className="left-section">
          <div className="search-bar">
            <div className="search-icon">
              <SearchIcon />
            </div>
            <input
              type="text"
              placeholder="Search here"
              value={searchInput}
              onChange={handleSearchChange}
            />
          </div>
        </div>
        <div className="right-section">
          <IconButton
            variant="primary"
            state="idle"
            type="button"
            width={"auto"}
            onClick={handleSupport}
          >
            Contact support
          </IconButton>
        </div>
      </div>

      <FAQList>
        {filtered.map(({ id, title }) => {
          // open if: user clicked this one, OR in search mode
          const isOpen = isSearchMode || openId === id;
          const detail = details[id];

          return (
            <FAQItem key={id}>
              <Question
                onClick={() => {
                  if (!isSearchMode) {
                    setOpenId(isOpen ? null : id);
                    if (!isOpen) dispatch(fetchDetail(id));
                  }
                }}
              >
                <span dangerouslySetInnerHTML={{ __html: highlight(title) }} />
                <Arrow open={isOpen}>
                  <DownArrowIcon />
                </Arrow>
              </Question>

              {isOpen && (
                <Answer>
                  {!detail ? (
                    <p>Loading…</p>
                  ) : (
                    <>
                      <div
                        dangerouslySetInnerHTML={{
                          __html: highlight(detail.content),
                        }}
                      />
                      <div
                        style={{ marginTop: 16 }}
                        dangerouslySetInnerHTML={{
                          __html: detail.video_iframe,
                        }}
                      />
                    </>
                  )}
                </Answer>
              )}
            </FAQItem>
          );
        })}
      </FAQList>
    </div>
  );
};

export default UserGuide;

const FAQList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
const FAQItem = styled.li`
  background: #fff;
  border: 1px solid #e1e1e1;
  border-radius: 8px;
  overflow: hidden;
`;
const Question = styled.div`
  padding: 16px;
  display: flex;
  justify-content: space-between;
  cursor: pointer;

  span {
    /* Apply h6 styles here */
    font-size: 20px;
    line-height: 24px;
    /* Apply semi-bold */
    font-weight: 600;
  }

  &:hover {
    background: #f0f0f0;
  }
`;
const Arrow = styled.span`
  display: inline-block;
  transition: transform 0.2s;
  transform: ${({ open }) => (open ? "rotate(180deg)" : "none")};
`;
const Answer = styled.div`
  padding: 0 16px 16px;
  line-height: 24px;
  img {
    width: 100%;
    margin-top: 16px;
    border-radius: 8px;
  }
`;
